import * as React from "react";

// ---------------------------------------------------------------------------
// BrowserFrame — fake browser chrome for showcasing full-page assemblies
// ---------------------------------------------------------------------------
export interface BrowserFrameProps {
  url?: string;
  minHeight?: number | string;
  children: React.ReactNode;
  className?: string;
}
export function BrowserFrame({
  url = "https://app.constructorfleet.com",
  minHeight,
  children,
  className = "",
}: BrowserFrameProps) {
  return (
    <div className={["browser-frame", className].filter(Boolean).join(" ")}>
      <div className="browser-frame-bar">
        <span className="browser-frame-dot" />
        <span className="browser-frame-dot" />
        <span className="browser-frame-dot" />
        <div className="browser-frame-url">{url}</div>
      </div>
      <div className="browser-frame-body" style={minHeight ? { minHeight } : undefined}>
        {children}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PhoneFrame
// ---------------------------------------------------------------------------
export interface PhoneFrameProps {
  width?: number;
  height?: number;
  dark?: boolean;
  time?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}
export function PhoneFrame({
  width = 402,
  height = 874,
  dark,
  time = "9:41",
  children,
  className = "",
}: PhoneFrameProps) {
  return (
    <div
      className={["phone-frame", dark && "dark", className].filter(Boolean).join(" ")}
      style={{ width, height }}
    >
      <div className="phone-status-bar" aria-hidden="true">
        <span className="phone-status-time">{time}</span>
        <span className="phone-status-island" />
        <span className="phone-status-icons">
          <svg viewBox="0 0 18 10" className="phone-status-icon">
            <rect x="0.5" y="5" width="2.5" height="4" rx="0.5" fill="currentColor" />
            <rect x="4.5" y="3.5" width="2.5" height="5.5" rx="0.5" fill="currentColor" />
            <rect x="8.5" y="2" width="2.5" height="7" rx="0.5" fill="currentColor" />
            <rect x="12.5" y="0.5" width="2.5" height="8.5" rx="0.5" fill="currentColor" />
          </svg>
          <svg viewBox="0 0 14 10" className="phone-status-icon">
            <path
              d="M7 9.2a1 1 0 100-2 1 1 0 000 2zM3.5 6.5a5 5 0 017 0M1.2 4.2a8.5 8.5 0 0111.6 0"
              stroke="currentColor"
              strokeWidth="1.2"
              strokeLinecap="round"
              fill="none"
            />
          </svg>
          <svg viewBox="0 0 24 10" className="phone-status-icon">
            <rect
              x="0.5"
              y="1"
              width="20"
              height="8"
              rx="1.8"
              stroke="currentColor"
              strokeWidth="1"
              fill="none"
            />
            <rect x="2" y="2.5" width="13" height="5" rx="0.6" fill="currentColor" />
            <rect x="21" y="3.5" width="1.5" height="3" rx="0.4" fill="currentColor" />
          </svg>
        </span>
      </div>
      <div className="phone-frame-content">{children}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// MobileAppHeader
// ---------------------------------------------------------------------------
export interface MobileAppHeaderProps {
  greeting?: React.ReactNode;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  trailing?: React.ReactNode;
  className?: string;
}
export function MobileAppHeader({
  greeting,
  title,
  subtitle,
  trailing,
  className = "",
}: MobileAppHeaderProps) {
  return (
    <header className={["m-hdr", className].filter(Boolean).join(" ")}>
      <div>
        {greeting && <div className="m-hdr-greet">{greeting}</div>}
        <h1 className="m-hdr-title">{title}</h1>
        {subtitle && <p className="m-hdr-sub">{subtitle}</p>}
      </div>
      {trailing}
    </header>
  );
}

// ---------------------------------------------------------------------------
// MobileTabBar
// ---------------------------------------------------------------------------
export interface TabItem {
  icon: React.ReactNode;
  label: React.ReactNode;
  active?: boolean;
  onPress?: () => void;
}
export interface MobileTabBarProps {
  items: TabItem[];
  className?: string;
}
export function MobileTabBar({ items, className = "" }: MobileTabBarProps) {
  return (
    <nav className={["m-tabs", className].filter(Boolean).join(" ")}>
      {items.map((t, i) => (
        <button
          key={i}
          className={["m-tab", t.active && "active"].filter(Boolean).join(" ")}
          onClick={t.onPress}
        >
          <span className="m-tab-ic">{t.icon}</span>
          <span className="m-tab-lb">{t.label}</span>
        </button>
      ))}
    </nav>
  );
}

// ---------------------------------------------------------------------------
// MobileCard
// ---------------------------------------------------------------------------
export interface MobileCardProps extends React.HTMLAttributes<HTMLDivElement> {
  tone?: "default" | "danger" | "success";
}
export function MobileCard({
  tone = "default",
  className = "",
  children,
  ...rest
}: MobileCardProps) {
  return (
    <div
      className={["m-card", tone !== "default" && `m-card-${tone}`, className]
        .filter(Boolean)
        .join(" ")}
      {...rest}
    >
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// MobileListRow
// ---------------------------------------------------------------------------
export interface MobileListRowProps {
  leading?: React.ReactNode;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  trailing?: React.ReactNode;
  onPress?: () => void;
}
export function MobileListRow({ leading, title, subtitle, trailing, onPress }: MobileListRowProps) {
  return (
    <button className="m-row" onClick={onPress}>
      {leading && <span className="m-row-lead">{leading}</span>}
      <div className="m-row-body">
        <div className="m-row-title">{title}</div>
        {subtitle && <div className="m-row-sub">{subtitle}</div>}
      </div>
      {trailing && <span className="m-row-trail">{trailing}</span>}
    </button>
  );
}
